import { useLayoutEffect, useMemo, useRef } from 'react';
import { BufferGeometry, InstancedMesh, Matrix4, Vector3, Quaternion, Euler } from 'three';
import type { HardwareGroup } from './instances';
import { boltHex1_4_20, boltHex5_16_18 } from './primitives/boltGeometry';
import { useSceneSelection } from '../selection/useSceneSelection';
import { SCALE_IN_TO_UNIT } from '../types';
import { MATERIALS } from '../materials';

const GEOMETRY_FACTORY: Record<string, () => BufferGeometry> = {
  'bolt-hex-1-4-20': boltHex1_4_20,
  'bolt-hex-5-16-18': boltHex5_16_18,
};

const VISUAL_SCALE = 5; // bolts ×5 to be visible from camera distance

export function BoltField({ group }: { group: HardwareGroup }) {
  const ref = useRef<InstancedMesh>(null);
  const factory = GEOMETRY_FACTORY[group.geometry];
  const geometry = useMemo(() => (factory ? factory() : null), [factory, group.geometry]);

  const material = MATERIALS.steelAnodized;

  useLayoutEffect(() => {
    if (!ref.current) return;
    const m = new Matrix4();
    group.placements.forEach((p, i) => {
      m.compose(
        new Vector3(
          p.position.x * SCALE_IN_TO_UNIT,
          p.position.y * SCALE_IN_TO_UNIT,
          p.position.z * SCALE_IN_TO_UNIT,
        ),
        new Quaternion().setFromEuler(new Euler(p.rotation.x, p.rotation.y, p.rotation.z)),
        new Vector3(VISUAL_SCALE, VISUAL_SCALE, VISUAL_SCALE),
      );
      ref.current!.setMatrixAt(i, m);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  }, [group.placements]);

  if (!geometry || group.placements.length === 0) return null;

  return (
    <instancedMesh
      ref={ref}
      args={[geometry, material, group.placements.length]}
      castShadow
      userData={{ partNumber: group.partNumber, kind: 'hardware-field', geometry: group.geometry }}
      onPointerOver={(e) => {
        e.stopPropagation();
        const id = e.instanceId;
        if (id == null) return;
        const ref = group.placements[id]?.parentRefId ?? group.partNumber;
        useSceneSelection.getState().setHovered(ref);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        useSceneSelection.getState().setHovered(null);
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        const id = e.instanceId;
        if (id == null) return;
        const ref = group.placements[id]?.parentRefId ?? group.partNumber;
        useSceneSelection.getState().setSelected(ref);
      }}
    />
  );
}
